import React from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import {
  PaymentCheckEntity,
  CheckStatusType,
} from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface RejectedChecksDialogProps {
  visible: boolean;
  onHide: () => void;
  rejectedChecks: PaymentCheckEntity[];
}

export const RejectedChecksDialog: React.FC<RejectedChecksDialogProps> = ({
  visible,
  onHide,
  rejectedChecks,
}) => {
  // Format currency
  const formatCurrency = (value: number) => {
    return '₪' + value.toLocaleString();
  };

  // Format date
  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('he-IL');
  };

  // Get status label
  const getStatusLabel = (status: CheckStatusType): string => {
    switch (status) {
      case CheckStatusType.PENDING:
        return 'ממתין להפקדה';
      case CheckStatusType.DEPOSITED:
        return 'הופקד';
      case CheckStatusType.CLEARED:
        return 'פורע';
      case CheckStatusType.BOUNCED:
        return 'נדחה';
      case CheckStatusType.CANCELLED:
        return 'בוטל';
      case CheckStatusType.EXPIRED:
        return 'פג תוקף';
      default:
        return 'לא ידוע';
    }
  };

  // Get status severity
  const getStatusSeverity = (
    status: CheckStatusType,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case CheckStatusType.CLEARED:
        return 'success';
      case CheckStatusType.DEPOSITED:
        return 'info';
      case CheckStatusType.PENDING:
        return 'warning';
      case CheckStatusType.BOUNCED:
      case CheckStatusType.CANCELLED:
      case CheckStatusType.EXPIRED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  // Status body template
  const statusBodyTemplate = (rowData: PaymentCheckEntity) => {
    const label = getStatusLabel(rowData.checkStatus);
    const severity = getStatusSeverity(rowData.checkStatus);
    return <Tag value={label} severity={severity} />;
  };

  // Bank details body template
  const bankDetailsBodyTemplate = (rowData: PaymentCheckEntity) => {
    return `${rowData.bankNumber}-${rowData.branchNumber}-${rowData.accountNumber}`;
  };

  // Due date body template
  const dueDateBodyTemplate = (rowData: PaymentCheckEntity) => {
    return formatDate(rowData.dueDate);
  };

  // Updated date body template
  const updatedDateBodyTemplate = (rowData: PaymentCheckEntity) => {
    return rowData.updatedAt ? formatDate(rowData.updatedAt) : '-';
  };

  // Updated by body template
  const updatedByBodyTemplate = (rowData: PaymentCheckEntity) => {
    if (rowData.updatedByFirstName && rowData.updatedByLastName) {
      return `${rowData.updatedByFirstName} ${rowData.updatedByLastName}`;
    }
    return '-';
  };

  // Reference ID body template
  const referenceIdBodyTemplate = (rowData: PaymentCheckEntity) => {
    return rowData.referenceId || '-';
  };

  return (
    <Dialog
      header="המחאות שחזרו"
      visible={visible}
      style={{ width: '95vw', maxWidth: '1200px' }}
      onHide={onHide}
      modal
    >
      <div className="p-4">
        <DataTable
          value={rejectedChecks}
          loading={false}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          showGridlines
          stripedRows
          responsiveLayout="scroll"
        >
          <Column
            field="id"
            header="מספר המחאה"
            sortable
            style={{ minWidth: '120px', textAlign: 'right' }}
          />

          <Column
            header="פרטי בנק"
            body={bankDetailsBodyTemplate}
            style={{ minWidth: '200px', textAlign: 'right' }}
          />

          <Column
            field="referenceId"
            header="מספר אסמכתא"
            body={referenceIdBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            field="dueDate"
            header="תאריך פירעון"
            body={dueDateBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            field="checkStatus"
            header="סטטוס"
            body={statusBodyTemplate}
            sortable
            style={{ minWidth: '120px', textAlign: 'right' }}
          />

          <Column
            field="updatedAt"
            header="תאריך עדכון"
            body={updatedDateBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            header="עודכן על ידי"
            body={updatedByBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />
        </DataTable>
      </div>
    </Dialog>
  );
};
